import { useEffect, useState } from "react";
import { api, formatCents, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Pill, Loader, EmptyState } from "@/components/ui/Primitives";
import { Wallet, ShieldCheck, Mail, Landmark, Link as LinkIcon, MapPin, StickyNote, Copy, CheckCircle2, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { downloadCsv } from "@/lib/clientCsv";

const InstructionRow = ({ icon: Icon, label, value, copyable = true, isLink = false, onCopy }) => {
  if (!value) return null;
  const testKey = label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`instr-row-${testKey}`}>
      <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-none">
        <Icon className="w-4 h-4"/>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
        {isLink ? (
          <a href={value} target="_blank" rel="noopener noreferrer"
             className="text-sm font-semibold text-emerald-700 hover:text-emerald-800 break-all inline-flex items-center gap-1">
            {value} <ExternalLink className="w-3 h-3"/>
          </a>
        ) : (
          <div className="text-sm font-semibold text-slate-900 whitespace-pre-wrap break-words mt-0.5">{value}</div>
        )}
      </div>
      {copyable && !isLink && (
        <button type="button" onClick={() => onCopy?.(value, label)}
                className="text-slate-500 hover:text-emerald-700 flex-none p-1"
                aria-label={`Copy ${label}`} data-testid={`copy-${testKey}`}>
          <Copy className="w-4 h-4"/>
        </button>
      )}
    </div>
  );
};

/**
 * Rent-payment page — peer-to-peer only.
 *
 * Central Rentals never routes tenant funds. Landlords publish their own
 * payment instructions (Interac e-Transfer, bank transfer details, PayPal
 * link, cheque address). Tenants view those instructions here and pay
 * their landlord directly. After paying, tenants can "Report" the payment
 * so their landlord confirms it in one tap — that's all the app does.
 *
 * The Stripe-Connect / platform-fee flow was removed Feb 2026 because
 * routing money between third parties is regulated as money-service-
 * business activity in Canada (FINTRAC / provincial licensing).
 */
export default function Payments() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [dues, setDues] = useState({ lease: null, dues: [] });
  const [instructions, setInstructions] = useState(null);
  const [loading, setLoading] = useState(true);

  const isTenant = user?.role === "tenant";

  const load = async () => {
    setLoading(true);
    try {
      const calls = [api.get("/payments").catch(() => ({ data: [] }))];
      if (isTenant) {
        calls.push(api.get("/payments/dues").catch(() => ({ data: { lease: null, dues: [] } })));
      }
      const res = await Promise.all(calls);
      setPayments(res[0].data || []);
      if (isTenant) {
        const duesData = res[1].data || { lease: null, dues: [] };
        setDues(duesData);
        // If we have a lease, load the landlord's payment instructions.
        if (duesData.lease?.lease_id) {
          try {
            const { data } = await api.get(`/rent/payment-instructions?lease_id=${encodeURIComponent(duesData.lease.lease_id)}`);
            setInstructions(data);
          } catch { setInstructions(null); }
        }
      } else {
        // Landlord view: load their own saved instructions.
        try {
          const { data } = await api.get("/rent/payment-instructions/mine");
          setInstructions(data);
        } catch { setInstructions(null); }
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [user?.role]);

  if (loading) return <Loader/>;

  const nextDue = (dues.dues || []).find((d) => d.status !== "paid");
  const hasInstructions = instructions && (
    instructions.interac_email || instructions.bank_details ||
    instructions.other_link  || instructions.mailing_address ||
    instructions.notes
  );

  const copyToClipboard = (value, label) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(
      () => toast.success(`${label} copied to clipboard`),
      () => toast.error("Could not copy — please long-press and copy manually")
    );
  };

  const reportPayment = async (due) => {
    if (!due?.due_id) return;
    try {
      await api.post(`/rent-dues/${due.due_id}/record-payment`, {
        method: "e_transfer",
        reference: null,
      });
      toast.success("Thanks — your landlord will confirm shortly.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record payment");
    }
  };

  return (
    <div>
      <PageHeader
        title={isTenant ? "Rent & Payments" : "Payments"}
        subtitle={isTenant
          ? "Pay your landlord directly using the instructions they've published below."
          : "Rent record. Central Rentals never holds tenant funds — tenants pay you directly."
        }
      />

      {/* Safety notice — makes the peer-to-peer model clear */}
      <Card className="p-4 mb-5 bg-emerald-50 border-emerald-200 flex items-start gap-3" data-testid="peer-to-peer-notice">
        <div className="w-9 h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center flex-none">
          <ShieldCheck className="w-4 h-4"/>
        </div>
        <div className="min-w-0 text-sm text-emerald-900">
          <div className="font-semibold">Peer-to-peer payments only</div>
          <div className="text-emerald-800/85 mt-0.5">
            Central Rentals never processes, holds, or transfers rent money.
            {isTenant ? " Send your rent directly to your landlord using the details below." :
                       " Publish your preferred payment method — tenants pay you directly."}
          </div>
        </div>
      </Card>

      {/* Tenant view */}
      {isTenant && (
        <>
          {nextDue ? (
            <Card className="p-6 mb-5 bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0 shadow-lg" data-testid="tenant-next-due-card">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-50">Next rent due</div>
              <div className="mt-2 font-display text-4xl font-bold">{formatCents(nextDue.amount)}</div>
              <div className="mt-1 text-emerald-50/90 text-sm">Due {formatDate(nextDue.due_date)} · Period {nextDue.period}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => reportPayment(nextDue)} data-testid="report-paid-button"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white text-emerald-700 font-semibold hover:bg-emerald-50 transition-colors shadow-sm">
                  <CheckCircle2 className="w-4 h-4"/> I&apos;ve paid — notify landlord
                </button>
              </div>
            </Card>
          ) : (
            <EmptyState icon={Wallet} title="You're all caught up"
                        description={dues.lease ? "No upcoming rent due." : "You don't have an active lease yet."} />
          )}

          <Card className="p-6 mb-6" data-testid="tenant-payment-instructions-card">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Your landlord&apos;s payment instructions</h2>
            <p className="text-sm text-slate-500 mb-3">Pay your landlord directly using any of the methods they&apos;ve listed.</p>
            {hasInstructions ? (
              <div className="divide-y divide-slate-100">
                <InstructionRow icon={Mail} label="Interac e-Transfer" value={instructions.interac_email} onCopy={copyToClipboard} />
                <InstructionRow icon={Landmark} label="Bank details" value={instructions.bank_details} onCopy={copyToClipboard} />
                <InstructionRow icon={LinkIcon} label="Payment link" value={instructions.other_link} isLink />
                <InstructionRow icon={MapPin} label="Mailing address" value={instructions.mailing_address} onCopy={copyToClipboard} />
                <InstructionRow icon={StickyNote} label="Notes from landlord" value={instructions.notes} copyable={false} />
              </div>
            ) : (
              <EmptyState icon={Mail} title="Not published yet"
                          description="Ask your landlord to publish their payment instructions in Central Rentals → Settings → Payment instructions." />
            )}
          </Card>
        </>
      )}

      {/* Landlord view */}
      {!isTenant && (
        <Card className="p-6 mb-6" data-testid="landlord-payment-instructions-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Your published payment instructions</h2>
              <p className="text-sm text-slate-500">Tenants see this in their Payments tab. Update anytime.</p>
            </div>
            <Link to="/settings/payment-instructions">
              <Button data-testid="edit-payment-instructions-button">
                {hasInstructions ? "Edit instructions" : "Publish instructions"}
              </Button>
            </Link>
          </div>
          <div className="mt-4">
            {hasInstructions ? (
              <div className="divide-y divide-slate-100">
                <InstructionRow icon={Mail} label="Interac e-Transfer" value={instructions.interac_email} onCopy={copyToClipboard} />
                <InstructionRow icon={Landmark} label="Bank details" value={instructions.bank_details} onCopy={copyToClipboard} />
                <InstructionRow icon={LinkIcon} label="Payment link" value={instructions.other_link} isLink />
                <InstructionRow icon={MapPin} label="Mailing address" value={instructions.mailing_address} onCopy={copyToClipboard} />
                <InstructionRow icon={StickyNote} label="Notes" value={instructions.notes} copyable={false} />
              </div>
            ) : (
              <EmptyState icon={Mail} title="Nothing published yet"
                          description="Add your Interac e-Transfer, bank details, or a payment link so tenants know where to send rent." />
            )}
          </div>
        </Card>
      )}

      {/* Payment history — read-only ledger of manual payments recorded */}
      <Card className="overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">Payment history</h2>
            <p className="text-sm text-slate-500">{payments.length} record{payments.length === 1 ? "" : "s"}</p>
          </div>
          {payments.length > 0 && (
            <button
              type="button"
              data-testid="payments-download-csv"
              onClick={() => {
                // Client-side CSV generation — zero server round-trip,
                // works fully offline, meets the LONGEVITY promise
                // (CSV format outlives any SaaS).
                const rows = payments.map((p) => ({
                  date: p.paid_at || p.created_at || "",
                  amount_cad: (p.amount_cents || 0) / 100,
                  method: p.method || "",
                  status: p.status || "",
                  tenant: p.tenant_email || "",
                  lease_id: p.lease_id || "",
                  note: p.note || "",
                }));
                downloadCsv(`payments-${new Date().toISOString().slice(0,10)}.csv`, rows);
                toast.success("Downloaded — CSV generated on-device");
              }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 hover:border-emerald-500 hover:text-emerald-700"
            >
              <Download className="w-3.5 h-3.5" /> Download CSV
            </button>
          )}
        </div>
        {payments.length === 0 ? (
          <div className="py-14 text-center text-sm text-slate-500">No payments recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 font-semibold">Period</th>
                  <th className="px-5 py-3 font-semibold">Date</th>
                  <th className="px-5 py-3 font-semibold">Amount</th>
                  <th className="px-5 py-3 font-semibold">Method</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, i) => (
                  <tr key={p.payment_id || i} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`payment-row-${i}`}>
                    <td className="px-5 py-4 font-semibold text-slate-900">{p.period || "—"}</td>
                    <td className="px-5 py-4 text-slate-700">{formatDate(p.paid_at || p.created_at)}</td>
                    <td className="px-5 py-4 font-semibold text-slate-900">{formatCents(p.amount)}</td>
                    <td className="px-5 py-4 text-slate-700 capitalize">{p.payment_method || "Manual"}</td>
                    <td className="px-5 py-4"><Pill tone={p.status === "succeeded" || p.status === "paid" ? "green" : "amber"}>{p.status}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
